import React, { useState, useEffect } from "react";
import SEO from "../seo";
const ManageInstalledApps = () => {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "overview",
        "launch-an-app",
        "pin-an-app-for-quick-access",
        "view-an-apps-privacy-policy",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Manage Installed Apps | xPage Help Center"
        description="Learn how to manage your installed apps on Xpage. Launch apps, pin frequently used apps for quick access, and review privacy policies to stay informed about data usage."
        keywords="Xpage apps, manage apps, launch app, pin app, app privacy, installed apps, app dashboard"
        canonical="https://help.xpage.ai/help/apps/Manage%20Installed%20Apps"
        type="article"
      />

      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Header Section */}
        <header id="overview" className="mb-6 scroll-mt-20">
          <h1 className="text-systemTitle text-2xl font-semibold mb-3">
            Manage Installed Apps 
          </h1>
          <p className="text-sm text-systemLabel">
            Once installed, Xpage apps can be easily managed from your
            dashboard. You can launch apps, pin frequently used apps for quick
            access, and review privacy policies to stay informed about data
            usage.
          </p>
        </header>

        {/* Launch an App Section */}
        <section id="launch-an-app" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Launch an App
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <p className="text-sm text-systemLabel">
                Access your installed apps directly from the Apps page in your
                Xpage dashboard.
              </p>

              <table className="w-full mb-4">
                <tbody className="border divide-y divide-systemBorder">
                  <tr className="bg-gray-400/10 transition-colors">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Step 1
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Log in to your Xpage dashboard and navigate to Apps.
                    </td>
                  </tr>
                  <tr className="">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Step 2
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Locate the app you want to open and click its name.
                    </td>
                  </tr>
                  <tr className="bg-gray-400/10 transition-colors">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Step 3
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      On the app page, click Launch App to start using it
                      immediately.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Pin an App for Quick Access Section */}
        <section id="pin-an-app-for-quick-access" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Pin an App for Quick Access
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <p className="text-sm text-systemLabel">
                Pinning apps keeps your most-used tools just a click away in
                your dashboard navigation.
              </p>

              <table className="w-full mb-4">
                <tbody className="border divide-y divide-systemBorder">
                  <tr className="bg-gray-400/10 transition-colors">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Step 1
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Open the app's details page from the Apps section.
                    </td>
                  </tr>
                  <tr className="">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Step 2
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Click the pin icon in the top-right corner to add it to
                      your navigation menu.
                    </td>
                  </tr>
                  <tr className="bg-gray-400/10 transition-colors">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        To Unpin
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Click the pin icon again in the navigation menu to remove
                      it.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* View an App's Privacy Policy Section */}
        <section id="view-an-apps-privacy-policy" className="scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            View an App's Privacy Policy
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <p className="text-sm text-systemLabel">
                Stay informed about each app's permissions and data usage to
                ensure transparency and security.
              </p>

              <table className="w-full mb-4">
                <tbody className="border divide-y divide-systemBorder">
                  <tr className="bg-gray-400/10 transition-colors">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Step 1
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Go to the Apps page in your Xpage dashboard.
                    </td>
                  </tr>
                  <tr className="">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Step 2
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Click the app's name to open its details page.
                    </td>
                  </tr>
                  <tr className="bg-gray-400/10 transition-colors">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Step 3
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Scroll to the Permission Details section.
                    </td>
                  </tr>
                  <tr className="">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Step 4
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Click View Privacy Policy to review how the app uses your
                      store data.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#overview"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "overview"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Overview
              </a>
            </li>
            <li>
              <a
                href="#launch-an-app"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "launch-an-app"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Launch an App
              </a>
            </li>
            <li>
              <a
                href="#pin-an-app-for-quick-access"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "pin-an-app-for-quick-access"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Pin an App for Quick Access
              </a>
            </li>
            <li>
              <a
                href="#view-an-apps-privacy-policy"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "view-an-apps-privacy-policy"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                View an App's Privacy Policy
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ManageInstalledApps;
